import { inject, Injectable } from '@angular/core';
import { FirebaseApp } from '@angular/fire/app';
import {
  Observable,
  firstValueFrom,
  shareReplay,
  switchMap,
} from 'rxjs';
import { ENVIRONMENT, ManualInvoice } from '@models';
import { FirestoreService } from './firestore.service';
import { resolveFunctionsBaseUrl } from './functions-base-url';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class ManualInvoiceService {
  public all$: Observable<ManualInvoice[]>;

  private readonly environment = inject<any>(ENVIRONMENT);
  private readonly firebaseApp = inject(FirebaseApp);

  private get functionsBaseUrl(): string {
    return resolveFunctionsBaseUrl(this.firebaseApp, this.environment);
  }

  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
  ) {
    this.all$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<ManualInvoice>(
          this.tenantContext.collections.MANUAL_INVOICES,
        ),
      ),
      shareReplay(1),
    );
  }

  /**
   * Returns the next sequential number for a given series + fiscal year.
   * Reads current invoices and returns max(number) + 1, or 1 if none exist.
   */
  async getNextNumber(series: string, fiscalYear: string): Promise<number> {
    const invoices = await firstValueFrom(this.all$);
    const matching = invoices.filter(
      (i) => i.series === series && i.fiscalYear === fiscalYear,
    );
    if (matching.length === 0) return 1;
    return Math.max(...matching.map((i) => i.number)) + 1;
  }

  /**
   * Builds the human-readable invoice number: '{series}-{year}-{number padded to 4 digits}'.
   * e.g. 'F-2026-0003'
   */
  buildInvoiceNumber(
    series: string,
    fiscalYear: string,
    number: number,
  ): string {
    return `${series}-${fiscalYear}-${String(number).padStart(4, '0')}`;
  }

  async create(
    data: Omit<ManualInvoice, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<string> {
    return this.firestore.create<ManualInvoice>(
      this.tenantContext.collections.MANUAL_INVOICES,
      data,
    );
  }

  async update(data: Partial<ManualInvoice> & { id: string }): Promise<void> {
    return this.firestore.update<ManualInvoice>(
      this.tenantContext.collections.MANUAL_INVOICES,
      data,
    );
  }

  async delete(id: string): Promise<void> {
    return this.firestore.delete(
      this.tenantContext.collections.MANUAL_INVOICES,
      id,
    );
  }

  downloadPdf(invoiceId: string): Promise<void> {
    const tenantId = this.tenantContext.tenantId;
    const url = `${this.functionsBaseUrl}manualInvoice?invoiceId=${invoiceId}&tenantId=${tenantId}`;
    // Navegación directa (no XHR): la Cloud Function devuelve el PDF con
    // Content-Disposition: attachment y no expone cabeceras CORS, así que una
    // petición fetch/XHR se bloquea. window.open evita CORS, igual que la
    // descarga de facturas normales (InvoicesService.downloadInvoice).
    window.open(url, '_blank');
    return Promise.resolve();
  }
}
